import { Button, getLink, Menu, Spacer, Spinner, theme, Title, toast } from '@metorial/ui';
import { RiArrowRightSLine, RiMore2Fill } from '@remixicon/react';
import copy from 'copy-to-clipboard';
import React from 'react';
import { styled } from 'styled-components';

type ItemGridItemMode = 'default' | 'compactHorizontal';
type ItemGridItemVariant = 'v1' | 'v2';

type ItemGridRootProps = React.ComponentPropsWithoutRef<'ul'> & {
  width?: string;
  columns?: number;
  responsive?: boolean;
  responsiveTwoColumnWidth?: string;
  responsiveOneColumnWidth?: string;
};

let GridContainer = styled.div`
  container-type: inline-size;
  min-width: 0;
  width: 100%;
`;

let Grid = styled.ul.withConfig({
  shouldForwardProp: p =>
    p !== 'width' &&
    p !== 'columns' &&
    p !== 'responsive' &&
    p !== 'responsiveTwoColumnWidth' &&
    p !== 'responsiveOneColumnWidth'
})<{
  width?: string;
  columns?: number;
  responsive?: boolean;
  responsiveTwoColumnWidth?: string;
  responsiveOneColumnWidth?: string;
}>`
  display: grid;
  grid-template-columns: ${p =>
    p.columns
      ? `repeat(${p.columns}, minmax(0, 1fr))`
      : `repeat(auto-fill, minmax(${p.width ?? '300px'}, 1fr))`};
  gap: 20px;
  list-style: none;
  padding: 0;
  margin: 0;

  ${p =>
    p.columns && p.responsive
      ? `
    @container (max-width: ${p.responsiveTwoColumnWidth ?? '920px'}) {
      grid-template-columns: repeat(${Math.min(p.columns, 2)}, minmax(0, 1fr));
    }

    @container (max-width: ${p.responsiveOneColumnWidth ?? '500px'}) {
      grid-template-columns: minmax(0, 1fr);
    }
  `
      : ''}
`;

let Wrapper = styled.li.withConfig({
  shouldForwardProp: p => p !== '$mode' && p !== '$disabled' && p !== '$variant'
})<{
  $mode?: ItemGridItemMode;
  $disabled?: boolean;
  $variant?: ItemGridItemVariant;
}>`
  position: relative;
  display: flex;
  flex-direction: column;
  width: 100%;
  padding: ${p =>
    p.$variant === 'v2' ? '0' : p.$mode === 'compactHorizontal' ? '12px' : '15px'};
  background: ${p => (p.$variant === 'v2' ? theme.colors.gray100 : 'none')};
  border: solid 1px ${p => (p.$variant === 'v2' ? theme.colors.gray400 : theme.colors.gray300)};
  border-radius: ${p =>
    p.$variant === 'v2' ? '8px' : p.$mode === 'compactHorizontal' ? '12px' : '15px'};
  box-shadow: ${p => (p.$variant === 'v2' ? theme.shadows.small : 'none')};
  color: inherit;
  font: inherit;
  text-align: left;
  text-decoration: none;
  /* BlackOS: no hover lift or shadow; the border darkens instantly. */
  transition: none;
  opacity: ${p => (p.$disabled ? 0.45 : 1)};

  &[data-button='true'] {
    cursor: pointer;

    &:hover,
    &:focus-visible,
    a:focus-visible & {
      border: solid 1px ${theme.colors.gray500};
    }
  }
`;

let LoadingIndicator = styled.div`
  position: absolute;
  top: 0px;
  left: 0px;
  right: 0px;
  bottom: 0px;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 999px;
`;

let Header = styled.header`
  display: flex;
  gap: 10px;
  justify-content: space-between;
  position: relative;
`;

let HeaderContent = styled.div.withConfig({ shouldForwardProp: p => p !== '$hasMenu' })<{
  $hasMenu: boolean;
}>`
  display: flex;
  flex-direction: column;
  gap: 5px;
  flex: 1;
  min-width: 0;
  max-width: 100%;
  padding-right: ${p => (p.$hasMenu ? '46px' : 0)};
`;

let IconSlot = styled.div`
  width: 100%;
`;

let CompactHeaderContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  flex: 1;
  min-width: 0;
`;

let CompactTitleRow = styled.div.withConfig({ shouldForwardProp: p => p !== '$hasMenu' })<{
  $hasMenu: boolean;
}>`
  display: flex;
  align-items: center;
  gap: 10px;
  padding-right: ${p => (p.$hasMenu ? '36px' : 0)};
  min-width: 0;
`;

let CompactTitleWrapper = styled.div`
  flex: 1;
  min-width: 0;

  h2 {
    overflow-wrap: anywhere;
  }
`;

let TitleWrapper = styled.div`
  min-width: 0;
  max-width: 100%;

  h2,
  h3 {
    max-width: 100%;
    overflow-wrap: anywhere;
    word-break: break-word;
  }
`;

let Description = styled.div`
  display: -webkit-box;
  overflow: hidden;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  margin: 0;
  padding: 0;
  color: ${theme.colors.gray700};
  font-family: inherit;
  font-size: 12px;
  font-weight: 500;
  letter-spacing: 0.0025em;
  line-height: 16px;
`;

let MenuWrapper = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  z-index: 1;
`;

let V2Content = styled.div.withConfig({ shouldForwardProp: p => p !== '$mode' })<{
  $mode?: ItemGridItemMode;
}>`
  position: relative;
  flex: 1;
  padding: ${p => (p.$mode === 'compactHorizontal' ? '12px' : '15px')};
  background: ${theme.colors.background};
  border: 1px solid ${theme.colors.gray400};
  border-radius: 8px;
  box-shadow: ${theme.shadows.small};
  margin: -1px -1px 10px -1px;
`;

let V2Footer = styled.footer`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 0 15px 12px 15px;
  min-height: 20px;
`;

let V2FooterLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
  flex: 1;
`;

let V2OpenAction = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: ${theme.colors.gray600};

  svg {
    height: 18px;
    width: 18px;
  }
`;

type ItemGridActionProps = {
  href?: string;
  onClick?: () => void;
  disabled?: boolean;
};

let getActionProps = ({ href, onClick, disabled }: ItemGridActionProps) => {
  if (onClick) {
    // Not a real <button>: the card holds the kebab menu's <button>, and buttons can't nest.
    return {
      role: 'button' as const,
      tabIndex: disabled ? -1 : 0,
      'aria-disabled': disabled || undefined,
      onClick: disabled ? undefined : onClick,
      onKeyDown: disabled
        ? undefined
        : (e: React.KeyboardEvent<HTMLElement>) => {
            if (e.target !== e.currentTarget) return;
            if (e.key !== 'Enter' && e.key !== ' ') return;
            e.preventDefault();
            onClick();
          },
      'data-button': 'true'
    };
  }

  if (href && !disabled) {
    return {
      as: 'span' as const,
      'data-button': 'true'
    };
  }

  return {};
};

let wrapAction = (
  children: React.ReactNode,
  href?: string,
  disabled?: boolean,
  nativeLink?: boolean
) => {
  if (!href || disabled) return children;

  let style = {
    color: 'inherit',
    display: 'flex',
    minWidth: 0,
    textDecoration: 'none'
  };

  if (nativeLink) {
    return (
      <a href={href} style={style}>
        {children}
      </a>
    );
  }

  let Link = getLink();

  return (
    <Link to={href} style={style}>
      {children}
    </Link>
  );
};

export let ItemGrid = {
  Root: ({ responsive, style, ...props }: ItemGridRootProps) => {
    let grid = <Grid responsive={responsive} style={style as any} {...props} />;

    if (!responsive) return grid;

    return <GridContainer>{grid}</GridContainer>;
  },
  Item: ({
    title,
    description,
    icon,
    entity,
    showCopyId = true,
    menu,
    href,
    onClick,
    bottom,
    mode = 'default',
    variant = 'v1',
    small,
    height,
    disabled = false,
    loading = false,
    nativeLink = false
  }: {
    title: React.ReactNode;
    description?: React.ReactNode;
    icon?: React.ReactNode;
    entity?: { id: string; hasUsage?: boolean };
    showCopyId?: boolean;
    menu?: { label: string; onClick: () => void }[];
    href?: string;
    onClick?: () => void;
    bottom?: React.ReactNode;
    mode?: ItemGridItemMode;
    variant?: ItemGridItemVariant;
    small?: boolean;
    height?: number;
    disabled?: boolean;
    loading?: boolean;
    nativeLink?: boolean;
  }) => {
    let wrapperRef = React.useRef<HTMLLIElement>(null);
    let isOpenable = !!(href || onClick);
    let baseMenuItems = [
      ...(entity && showCopyId ? [{ id: 'id', label: 'Copy ID' }] : []),
      ...(menu?.map((item, i) => ({ id: String(i), label: item.label })) ?? [])
    ];
    let menuItems =
      baseMenuItems.length > 0 && isOpenable
        ? [{ id: 'open', label: 'Open' }, ...baseMenuItems]
        : baseMenuItems;

    let header = (
      <Header>
        {mode === 'compactHorizontal' ? (
          <CompactHeaderContent>
            <CompactTitleRow $hasMenu={menuItems.length > 0}>
              {icon}

              <CompactTitleWrapper>
                <Title as="h2" size="3" weight="strong">
                  {title}
                </Title>
              </CompactTitleWrapper>
            </CompactTitleRow>

            {description && <Description>{description}</Description>}
          </CompactHeaderContent>
        ) : (
          <HeaderContent $hasMenu={menuItems.length > 0}>
            {icon && <IconSlot>{icon}</IconSlot>}

            <TitleWrapper>
              <Title as="h2" size={small ? '3' : '4'} weight="strong">
                {title}
              </Title>
            </TitleWrapper>
            {description && <Description>{description}</Description>}
          </HeaderContent>
        )}

        {menuItems.length > 0 && (
          <MenuWrapper
            onClick={e => {
              e.preventDefault();
              e.stopPropagation();
            }}
          >
            <Menu
              onItemClick={id => {
                if (id === 'open') {
                  if (onClick) onClick();
                  else wrapperRef.current?.closest('a')?.click();
                } else if (id == 'id' && entity) {
                  copy(entity.id);
                  toast.success('Copied to clipboard');
                } else {
                  menu?.find((_, i) => String(i) === id)?.onClick();
                }
              }}
              items={menuItems}
            >
              <Button size="2" iconLeft={<RiMore2Fill />} title="More" variant="outline" />
            </Menu>
          </MenuWrapper>
        )}
      </Header>
    );

    return wrapAction(
      <Wrapper
        ref={wrapperRef}
        {...getActionProps({ href, onClick, disabled })}
        $mode={mode}
        $disabled={disabled}
        $variant={variant}
        aria-busy={loading}
        style={{
          height: mode === 'compactHorizontal' ? height : undefined,
          minHeight: height ?? (mode === 'compactHorizontal' || small ? 'unset' : 200),
          overflow: mode === 'compactHorizontal' && height ? 'hidden' : undefined
        }}
      >
        {loading && (
          <LoadingIndicator>
            <Spinner size={20} />
          </LoadingIndicator>
        )}

        {variant === 'v2' ? (
          <>
            <V2Content $mode={mode}>{header}</V2Content>

            {(bottom || isOpenable) && (
              <V2Footer>
                <V2FooterLeft>{bottom}</V2FooterLeft>

                {isOpenable && (
                  <V2OpenAction>
                    <RiArrowRightSLine />
                  </V2OpenAction>
                )}
              </V2Footer>
            )}
          </>
        ) : (
          <>
            {header}

            {bottom && (
              <>
                <Spacer />
                {bottom}
              </>
            )}
          </>
        )}
      </Wrapper>,
      onClick ? undefined : href,
      disabled,
      nativeLink
    );
  },
  CenteredItem: ({
    title,
    description,
    icon,
    href,
    onClick,
    disabled = false,
    loading = false,
    nativeLink = false
  }: {
    title: React.ReactNode;
    description?: React.ReactNode;
    icon: React.ReactNode;
    href?: string;
    onClick?: () => void;
    disabled?: boolean;
    loading?: boolean;
    nativeLink?: boolean;
  }) => {
    return wrapAction(
      <Wrapper
        {...getActionProps({ href, onClick, disabled })}
        $disabled={disabled}
        aria-busy={loading}
        style={{
          alignItems: 'center',
          justifyContent: 'center',
          gap: 15,
          minHeight: 200
        }}
      >
        {loading && (
          <LoadingIndicator>
            <Spinner size={20} />
          </LoadingIndicator>
        )}

        <div>{icon}</div>

        <TitleWrapper>
          <Title as="h3" size="4" weight="strong">
            {title}
          </Title>
        </TitleWrapper>

        {description && <Description>{description}</Description>}
      </Wrapper>,
      onClick ? undefined : href,
      disabled,
      nativeLink
    );
  },
  RawItem: Wrapper
};
